import React, { useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Package, Clock, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

interface PrimitiveCallNodeData {
  primitive?: any; // Full primitive definition
  primitiveId?: string;
  primitiveCall?: {
    primitiveId?: string;
    inputMapping?: Record<string, string>;
    outputMapping?: Record<string, string>;
  };
  primitive_type?: string;
  primitive_name?: string;
  inputMapping?: Record<string, string>;
  outputMapping?: Record<string, string>;
  parameters?: Record<string, any>; // Legacy field
  estimated_duration?: number;
  complexity_level?: 'basic' | 'intermediate' | 'advanced';
  description?: string;
  validation_state?: 'valid' | 'warning' | 'error';
  validation_messages?: string[];
}

const ComplexityBadge = ({ level }: { level?: string }) => {
  const variant =
    level === 'advanced' ? 'destructive' :
    level === 'intermediate' ? 'outline' :
    'secondary';

  const className =
    level === 'advanced' ? '' :
    level === 'intermediate' ? 'border-status-warning text-status-warning' :
    'bg-status-success text-white';

  return (
    <Badge variant={variant as any} className={className}>
      {level || 'basic'}
    </Badge>
  );
};

export default function PrimitiveCallNode({
  data,
  selected
}: {
  data: PrimitiveCallNodeData;
  selected?: boolean
}) {
  // BUG-014 FIX: Fetch primitive name if missing but primitiveId exists
  const [primitiveName, setPrimitiveName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Get primitiveId from either top-level or nested primitiveCall structure
    const primitiveId = data.primitiveCall?.primitiveId || data.primitiveId;

    // If we already have a name or no ID, skip fetch
    if (data.primitive_name || !primitiveId || isLoading) {
      return;
    }

    // Fetch primitive name from Firestore
    const fetchPrimitiveName = async () => {
      setIsLoading(true);
      try {
        // Try primitives collection first (global primitives)
        const primitiveRef = doc(db, 'primitives', primitiveId);
        const primitiveSnap = await getDoc(primitiveRef);

        if (primitiveSnap.exists()) {
          setPrimitiveName(primitiveSnap.data().name);
        } else {
          // Try flows collection (org-scoped scenarios saved as primitives)
          const flowRef = doc(db, 'flows', primitiveId);
          const flowSnap = await getDoc(flowRef);

          if (flowSnap.exists()) {
            setPrimitiveName(flowSnap.data().name);
          }
        }
      } catch (error) {
        console.error('Failed to fetch primitive name:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrimitiveName();
  }, [data.primitive_name, data.primitiveCall?.primitiveId, data.primitiveId, isLoading]);

  const borderColor =
    data.validation_state === 'error' ? 'border-status-error' :
    data.validation_state === 'warning' ? 'border-status-warning' :
    selected ? 'border-category-voice shadow-glow-focus' :
    'border-category-voice/50 dark:border-category-voice/30';

  const bgColor =
    data.validation_state === 'error' ? 'bg-status-error/5' :
    data.validation_state === 'warning' ? 'bg-status-warning/5' :
    'bg-card';

  // Use fetched name if primitive_name is missing
  const displayName = data.primitive_name || primitiveName || 'Primitive Call';

  return (
    <div className={cn(
      "min-w-[170px] rounded-lg border-2 transition-all duration-fast shadow-md hover:shadow-lg",
      borderColor,
      bgColor
    )}>
      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !border-2 !bg-category-voice !border-category-voice"
        style={{ left: '-6px' }}
      />

      {/* Node Header */}
      <div className="flex items-center justify-between gap-2 p-3 bg-gradient-to-r from-category-voice-bg to-category-diagnostics-bg border-b border-border dark:border-border-subtle">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-6 h-6 rounded bg-category-voice-bg flex items-center justify-center">
            <Package className="w-3.5 h-3.5 text-category-voice" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-foreground truncate">
              {displayName}
            </div>
            <div className="text-xs text-muted-foreground/70 truncate">
              {data.primitive_type || 'Call reusable primitive'}
            </div>
          </div>
        </div>
        <ComplexityBadge level={data.complexity_level} />
      </div>

      {/* Node Body */}
      <div className="p-3 space-y-2">
        {/* Description */}
        {data.description && typeof data.description === 'string' && (
          <div className="text-xs text-muted-foreground italic">
            {data.description}
          </div>
        )}

        {/* Input/Output Mappings */}
        {data.inputMapping && Object.keys(data.inputMapping).length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-muted-foreground/70 mb-1">
              Inputs:
            </div>
            {Object.entries(data.inputMapping).slice(0, 2).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground font-medium">
                  {key}:
                </span>
                <span className="text-foreground truncate font-mono">
                  {String(value)}
                </span>
              </div>
            ))}
            {Object.keys(data.inputMapping).length > 2 && (
              <div className="text-xs text-muted-foreground/70">
                +{Object.keys(data.inputMapping).length - 2} more
              </div>
            )}
          </div>
        )}

        {/* Output Mappings */}
        {data.outputMapping && Object.keys(data.outputMapping).length > 0 && (
          <div className="space-y-1 mt-2">
            <div className="text-xs font-semibold text-muted-foreground/70 mb-1">
              Outputs:
            </div>
            {Object.entries(data.outputMapping).slice(0, 2).map(([key, value]) => (
              <div key={key} className="flex justify-between gap-2 text-xs">
                <span className="text-muted-foreground font-medium">
                  {key}:
                </span>
                <span className="text-foreground truncate font-mono">
                  {String(value)}
                </span>
              </div>
            ))}
            {Object.keys(data.outputMapping).length > 2 && (
              <div className="text-xs text-muted-foreground/70">
                +{Object.keys(data.outputMapping).length - 2} more
              </div>
            )}
          </div>
        )}

        {/* No Primitive Selected */}
        {!data.primitiveCall?.primitiveId && !data.primitiveId && !data.primitive_name && !primitiveName && (
          <div className="text-xs text-muted-foreground/70 italic">
            No primitive selected
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border dark:border-border-subtle">
          <div className="flex items-center gap-1 text-xs text-muted-foreground/70">
            <Clock className="w-3 h-3" />
            <span>~{data.estimated_duration ? Math.round(data.estimated_duration / 1000) : 0}s</span>
          </div>
          {data.validation_state && data.validation_state !== 'valid' && (
            <AlertTriangle className={cn(
              "w-4 h-4",
              data.validation_state === 'error' ? 'text-status-error' : 'text-status-warning'
            )} />
          )}
        </div>

        {/* Validation Messages */}
        {data.validation_messages && data.validation_messages.length > 0 && (
          <div className="pt-2 border-t border-border dark:border-border-subtle space-y-1">
            {data.validation_messages.slice(0, 2).map((msg, idx) => (
              <div key={idx} className="text-xs text-status-error flex items-start gap-1">
                <span>•</span>
                <span>{msg}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connection Handles */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !border-2 !bg-category-voice !border-category-voice"
        style={{ right: '-6px' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="error"
        className="!w-3 !h-3 !border-2 !bg-status-error !border-status-error"
        style={{ bottom: '-6px' }}
      />
    </div>
  );
}

